// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import { generatePaddingStyles } from "@synd/App/EditorTemplates/PaddingField";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { CustomFieldDefinition } from "@synd/CustomFieldDefinition";
import { DivWrapper } from "@synd/XDL/constants";
import systemColors from "@synd/XDL/PreStyle/colors";
import { color, getAllColorsAsDropdownOptions, layoutContainer, xClassNames, xScreen } from "@synd/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    isFullWidth = "isFullWidth",
    fullWidthStylingType = "fullWidthStylingType",
    fullWidthColor = "fullWidthColor",
    fullWidthImage = "fullWidthImage",
    fullWidthImageFocalPoint = "fullWidthImageFocalPoint",
    customCSS = "customCSS",
    // XNG-Changes
    border = "border",
    borderValue = "borderValue",
    borderColor = "borderColor",
    padding = "padding",
    rowPadding = "rowPadding",
    bgColor = "bgColor",
    customBgColor = "customBgColor",
    alphaColor = "alphaColor",
    containerFullWidth = "containerFullWidth",
    contentFullWidth = "contentFullWidth",
    makeItemsTakeFullHeight = "makeItemsTakeFullHeight",
    makeItemsTakeFullWidth = "makeItemsTakeFullWidth",
    horizontalAlignment = "horizontalAlignment",
    allowOverlapOn = "allowOverlapOn",
    overlapPosition = "overlapPosition",
    overlapValue = "overlapValue",
    selectOverlapItem = "selectOverlapItem",
    rowId = "rowId",
    tabletGap = "tabletGap",
}

export type HorizontalAlignment = "left" | "center" | "right";
interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.isFullWidth]: boolean;
        [fields.fullWidthStylingType]: string;
        [fields.fullWidthColor]: string;
        [fields.fullWidthImage]: string;
        [fields.fullWidthImageFocalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.border]: boolean;
        [fields.borderValue]: number;
        [fields.borderColor]: string;
        [fields.padding]: boolean;
        [fields.rowPadding]: string;
        [fields.bgColor]: string;
        [fields.customBgColor]: string;
        [fields.alphaColor]: string;
        [fields.containerFullWidth]: boolean;
        [fields.contentFullWidth]: boolean;
        [fields.makeItemsTakeFullHeight]: boolean;
        [fields.makeItemsTakeFullWidth]: boolean;
        [fields.horizontalAlignment]: HorizontalAlignment[];
        [fields.allowOverlapOn]: string;
        [fields.overlapPosition]: string;
        [fields.overlapValue]: string;
        [fields.selectOverlapItem]: string;
        [fields.rowId]: string;
        [fields.tabletGap]: number;
    };
}

interface RowStyles {
    gridItem?: GridItemProps;
    // xng-change
    container?: GridContainerProps;
}

const rowStyles: RowStyles = {
    gridItem: {
        css: css`
            ${({ makeItemsTakeFullHeight }: any) =>
                makeItemsTakeFullHeight &&
                css`
                    & ${DivWrapper} {
                        height: 100%;
                    }
                `}
            ${({ horizontalAlign }: any) =>
                horizontalAlign === "center"
                    ? css`
                          display: flex;
                          justify-content: center;
                      `
                    : horizontalAlign === "right"
                      ? css`
                            display: flex;
                            justify-content: flex-end;
                        `
                      : horizontalAlign === "left" &&
                        css`
                            display: flex;
                            justify-content: flex-start;
                        `}
            div[class^="ZoneWrapper"] {
                display: flex;
                flex-direction: column;
                ${({ align }: any) =>
                    align === "middle"
                        ? css`
                              justify-content: center;
                          `
                        : align === "top"
                          ? css`
                                justify-content: flex-start;
                            `
                          : align === "bottom" &&
                            css`
                                justify-content: flex-end;
                            `}
                ${({ horizontalAlign }: any) =>
                    horizontalAlign === "center"
                        ? css`
                              align-items: center;
                          `
                        : horizontalAlign === "right"
                          ? css`
                                align-items: flex-end;
                            `
                          : horizontalAlign === "left" &&
                            css`
                                align-items: flex-start;
                            `}
                        div[class^="AddContainer"] {
                    width: 100%;
                }
            }
        `,
    },
    // xng-change
    container: {
        css: css`
            ${({
                allowOverlapOn,
                backgroundColor,
                overlapPosition,
                overlapValue,
                containerFullWidth,
                gradient,
                startColor,
                degree,
                endColor,
                alphaColor,
            }: any) => {
                const defaultCss = css`
                    ${containerFullWidth
                        ? css`
                              width: 100%;
                          `
                        : css`
                              ${layoutContainer}
                          `}

                    background-color: ${backgroundColor ? color(backgroundColor, parseFloat(alphaColor)) : ""};
                `;
                if (allowOverlapOn === "none") {
                    return defaultCss;
                }

                let cssStyle: any = css``;
                let cssMobileStyle: any = css``;
                if (overlapPosition === "both") {
                    cssStyle = css`
                        background: linear-gradient(
                            to bottom,
                            transparent 0%,
                            transparent ${(100 - overlapValue) / 2}%,
                            ${backgroundColor} ${(100 - overlapValue) / 2}%,
                            ${backgroundColor} ${overlapValue + (100 - overlapValue) / 2}%,
                            transparent ${overlapValue + (100 - overlapValue) / 2}%,
                            transparent 100%
                        );
                    `;
                } else if (overlapPosition === "top") {
                    cssStyle = css`
                        background: linear-gradient(
                            to bottom,
                            ${backgroundColor} ${overlapValue}%,
                            transparent ${overlapValue}%
                        );
                    `;
                } else if (overlapPosition === "bottom") {
                    cssStyle = css`
                        background: linear-gradient(
                            to bottom,
                            transparent ${overlapValue}%,
                            ${backgroundColor} ${overlapValue}%
                        );
                    `;
                }
                if (allowOverlapOn === "wideScreen") {
                    cssMobileStyle = css`
                        ${xScreen("lte", "sm")} {
                            background: ${backgroundColor};
                        }
                    `;
                }

                return css`
                    ${defaultCss}
                    ${cssStyle}
                    ${cssMobileStyle}
                `;
            }}
        `,
    },
};

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [12, 12, 12, 3, 3], // xng-change-CES-267
    4: [12, 12, 4, 4, 4], // xng-change-CES-253
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 12, 9, 9], // xng-change-CES-267
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            horizontalAlignment,
            reflow,
            // isFullWidth,
            // fullWidthStylingType,
            // fullWidthColor,
            // fullWidthImage,
            // fullWidthImageFocalPoint,
            customCSS,
            border,
            borderValue,
            borderColor,
            padding,
            rowPadding,
            bgColor,
            customBgColor,
            alphaColor,
            containerFullWidth,
            contentFullWidth,
            makeItemsTakeFullHeight,
            allowOverlapOn,
            overlapPosition,
            overlapValue,
            rowId,
            selectOverlapItem,
            // XNG-Changes:
            tabletGap,
        },
        id,
    } = props;
    const columnElements: JSX.Element[] = [];
    // XNG-Changes: adding overlap , styles and isTabletScreen.
    const styles = rowStyles;
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        const horizontalAlign = horizontalAlignment?.[x] ? horizontalAlignment[x] : "";
        // XNG-Changes: adding overlap
        const isOverlapItem =
            allowOverlapOn && (parseInt(selectOverlapItem, 10) - 1 === x || selectOverlapItem === "all");

        columnElements.push(
            <GridItem
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                align={align}
                // XNG-Changes
                className={xClassNames.append(isOverlapItem ? "overlap" : "no-overlap")}
                {...{
                    horizontalAlign,
                    makeItemsTakeFullHeight,
                    "data-overlap": isOverlapItem ? overlapValue : undefined,
                }}
                {...styles.gridItem}
            >
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    const solidBackgroundColor = bgColor === "customColor" ? customBgColor : bgColor;
    // const fullWidthBackgroundCss = !contentFullWidth
    //     ? undefined
    //     : fullWidthStylingType === "solidColor"
    //     ? css`
    //           background-color: ${fullWidthColor};
    //       `
    //     : fullWidthStylingType === "image"
    //     ? css`
    //           background-image: url("${fullWidthImage}");
    //           background-size: cover;
    //           ${getFocalPointStyles(fullWidthImageFocalPoint)}
    //       `
    //     : undefined;
    const gridContainerCss = css`
        &&&& {
            ${padding && rowPadding && `${generatePaddingStyles(rowPadding)};`}
            ${!contentFullWidth ? layoutContainer : ""}
            ${border && `border: ${borderValue};`}
            ${border && `border-color: ${borderColor};`}
        }
    `;

    const finalCss = css`
        ${gridContainerCss}
        ${customCSS}
    `;

    return (
        <>
            <StyledWrapper
                {...styles.container}
                {...{
                    allowOverlapOn,
                    overlapPosition,
                    overlapValue,
                    backgroundColor: solidBackgroundColor,
                    containerFullWidth,
                    alphaColor,
                }}
                id={rowId}
            >
                <GridContainer
                    css={finalCss}
                    // XNG-Changes
                    gap={(isTabletScreen ? tabletGap : gap) || 0}
                    isFullWidth={contentFullWidth}
                >
                    {columnElements}
                </GridContainer>
            </StyledWrapper>
        </>
    );
};

const defaultCustomCss = `& {
}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};
const customTab = {
    displayName: "Options",
    sortOrder: 1,
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 2,
            },
            {
                name: fields.contentFullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                // isVisible: (_item, _advancedFeaturesEnabled, theme) => !!theme?.content?.isFullWidth,
                sortOrder: 3,
                tab: customTab,
            },
            {
                name: fields.fullWidthStylingType,
                displayName: "Styling",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Solid Color",
                        value: "solidColor",
                    },
                    {
                        displayName: "Image",
                        value: "image",
                    },
                ],
                defaultValue: "solidColor",
                horizontal: true,
                fieldType: "General",
                isVisible: ({ fields: { contentFullWidth } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!contentFullWidth,
                sortOrder: 4,
            },
            {
                name: fields.fullWidthColor,
                displayName: "Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "transparent",
                fieldType: "General",
                isVisible: ({ fields: { contentFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!contentFullWidth && fullWidthStylingType === "solidColor",
                sortOrder: 5,
            },
            {
                name: fields.fullWidthImage,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: ({ fields: { contentFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!contentFullWidth && fullWidthStylingType === "image",
                sortOrder: 6,
            },
            {
                name: fields.fullWidthImageFocalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                fieldType: "General",
                hideEmptyOption: true,
                isVisible: ({ fields: { contentFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!contentFullWidth && fullWidthStylingType === "image",
                sortOrder: 7,
            },
            {
                name: fields.alignment,
                displayName: "Vertical Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            // XNG-Changes: adding horizontal alignment
            {
                name: fields.horizontalAlignment,
                displayName: "Horizontal Alignment",
                editorTemplate: "HorizontalAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 3,
                tab: advancedTab,
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "DropDownField",
                options: [
                    ...getAllColorsAsDropdownOptions(systemColors),
                    {
                        displayName: "Custom color",
                        value: "customColor",
                    },
                ],
                defaultValue: "",
                fieldType: "General",
                isVisible: (item: HasFields) => !item.fields.gradient,
                tab: advancedTab,
            },
            {
                name: fields.customBgColor,
                displayName: "Custom color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                tab: customTab,
                isVisible: (item: HasFields) => item.fields.bgColor === "customColor",
            },
            {
                name: fields.alphaColor,
                displayName: "Color - Opacity",
                editorTemplate: "TextField",
                defaultValue: "1",
                fieldType: "General",
                placeholder: "add color opacity",
                tab: advancedTab,
                isVisible: (item: HasFields) => item.fields.bgColor,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return isNumeric(value) && parseFloat(value) <= 1 && parseFloat(value) > 0 ? null : `Invalid Input`;
                },
            },
            {
                name: fields.border,
                displayName: "Border",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.borderValue,
                displayName: "Border",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                placeholder: "Enter: border style",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return CSS.supports("border", value) ? null : `Invalid Input`;
                },
                isVisible: item => item.fields.border,
            },
            {
                name: fields.borderColor,
                editorTemplate: "ColorPickerField",
                defaultValue: systemColors.common.border,
                fieldType: "General",
                tab: customTab,
                isVisible: item => item.fields.border,
            },
            {
                name: fields.padding,
                displayName: "Padding",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.rowPadding,
                displayName: "Padding Size",
                editorTemplate: "PaddingField",
                fieldType: "General",
                defaultValue: {} as any,
                isRequired: true,
                tab: customTab,
                isVisible: (item: any) => item.fields.padding,
            },
            {
                name: fields.makeItemsTakeFullHeight,
                displayName: "Show all items with the same height",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            // XNG-Changes: adding make columns same width
            {
                name: fields.makeItemsTakeFullWidth,
                displayName: "Show all items with the same width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.containerFullWidth,
                displayName: "Container Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customTab,
            },
            // XNG-Changes: adding overlap
            {
                name: fields.allowOverlapOn,
                displayName: "Allow Overlap",
                editorTemplate: "DropDownField",
                defaultValue: "none",
                hideEmptyOption: true,
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Wide Screen Only",
                        value: "wideScreen",
                    },
                ],
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.selectOverlapItem,
                displayName: "Select Overlap Column",
                editorTemplate: "SelectOverlapItemTemplate",
                defaultValue: "all",
                hideEmptyOption: true,
                fieldType: "General",
                isVisible: item => item.fields.allowOverlapOn !== "none",
                tab: customTab,
            },
            // XNG-Changes: adding row id
            {
                name: fields.rowId,
                displayName: "Row Id",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.overlapPosition,
                displayName: "Overlap side (from)",
                editorTemplate: "DropDownField",
                hideEmptyOption: true,
                fieldType: "General",
                options: [
                    { displayName: "top", value: "top" },
                    { displayName: "bottom", value: "bottom" },
                ],
                defaultValue: "bottom",
                isRequired: true,
                isVisible: item => item.fields.allowOverlapOn !== "none",
                tab: customTab,
            },
            {
                name: fields.overlapValue,
                displayName: "Overlap Value %",
                editorTemplate: "IntegerField",
                defaultValue: 52,
                max: 100,
                isRequired: true,
                fieldType: "General",
                isVisible: item => item.fields.allowOverlapOn !== "none",
                tab: customTab,
            },
            {
                name: fields.tabletGap,
                displayName: "Tablet Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 2,
            },
            // </XNG-Changes>
        ],
    },
};

export default widgetModule;
